import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { extractErrorMessage } from "../api/client";
import { formatTs } from "../api/workflowSummary";
import {
  formatTtl,
  listSearchEnvs,
  SearchEnv,
  SEARCH_ENV_STATUS_COLOR,
  SearchEnvStatus,
  TERMINAL_SEARCH_ENV_STATUSES,
  ttlChipSeverity,
  ttlRemainingMs,
} from "../api/searchEnvs";

// Standalone /search-envs page — every env the authenticated user
// can see (theirs as a user, everyone's as an admin). Polls list
// every 15s while the page is open so an env transitioning from
// provisioning → indexing → ready updates without a manual refresh.
//
// Detail / actions live on /exports/<workflowId> for now (via the
// SearchEnvPanel on workflow-detail). This page is the "manage
// across exports" surface — find any env regardless of which
// workflow produced it.

function fmtUsd(n: number | null | undefined): string {
  if (n === undefined || n === null) return "—";
  return `$${n.toFixed(2)}`;
}

export function SearchEnvsList() {
  const [rows, setRows] = useState<SearchEnv[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Live-tick the TTL countdown chips even when the underlying row
  // hasn't changed. Cheap state bump every 30s; the row data itself
  // refreshes on the 15s poll below.
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(h);
  }, []);

  const load = useCallback(() => {
    listSearchEnvs({ limit: 200 })
      .then((r) => setRows(r.search_envs))
      .catch((err) => setError(extractErrorMessage(err)));
  }, []);

  useEffect(() => {
    load();
    const h = setInterval(load, 15_000);
    return () => clearInterval(h);
  }, [load]);

  if (error)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  if (rows === null)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Container>
    );

  // Order: active envs first (provisioning, indexing, ready), then
  // terminal — within each group, most-recently-submitted first.
  const sorted = [...rows].sort((a, b) => {
    const aTerm = TERMINAL_SEARCH_ENV_STATUSES.has(a.status as SearchEnvStatus);
    const bTerm = TERMINAL_SEARCH_ENV_STATUSES.has(b.status as SearchEnvStatus);
    if (aTerm !== bTerm) return aTerm ? 1 : -1;
    return b.submitted_at - a.submitted_at;
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Typography variant="h4">Search environments</Typography>
        <Typography variant="body2" color="text.secondary">
          Temporary OpenSearch clusters provisioned from completed exports.
          Each env auto-reaps at its TTL.
        </Typography>
      </Box>
      {sorted.length === 0 ? (
        <Alert severity="info">
          No search envs yet. Open a completed{" "}
          <Link component={RouterLink} to="/exports">
            condensed-json export
          </Link>{" "}
          and click "Open for analysis" to create one.
        </Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>env</TableCell>
              <TableCell>status</TableCell>
              <TableCell>TTL left</TableCell>
              <TableCell>data</TableCell>
              <TableCell>class</TableCell>
              <TableCell align="right">cost so far</TableCell>
              <TableCell>workflow</TableCell>
              <TableCell>submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((env) => (
              <Row key={env.id} env={env} />
            ))}
          </TableBody>
        </Table>
      )}
    </Container>
  );
}

function Row({ env }: { env: SearchEnv }) {
  const terminal = TERMINAL_SEARCH_ENV_STATUSES.has(
    env.status as SearchEnvStatus,
  );
  const ttlMs = ttlRemainingMs(env);
  const sev = ttlChipSeverity(ttlMs);
  const ttlColor =
    sev === "red" ? "error" : sev === "amber" ? "warning" : "default";
  return (
    <TableRow hover>
      <TableCell>
        <Link
          component={RouterLink}
          to={`/search-envs/${env.id}`}
          variant="body2"
          sx={{ fontFamily: "monospace" }}
        >
          {env.id}
        </Link>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={env.status}
          color={SEARCH_ENV_STATUS_COLOR[env.status as SearchEnvStatus]}
        />
      </TableCell>
      <TableCell>
        {terminal ? (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ) : (
          <Chip
            size="small"
            label={formatTtl(ttlMs)}
            color={ttlColor}
            variant={sev === "ok" ? "outlined" : "filled"}
          />
        )}
      </TableCell>
      <TableCell>
        {env.data_size_gib !== null && env.data_size_gib !== undefined
          ? `${env.data_size_gib.toFixed(1)} GiB`
          : "—"}
      </TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
          {env.instance_class}
          {env.instance_count && env.instance_count > 1
            ? ` × ${env.instance_count}`
            : ""}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Tooltip
          title={
            env.projected_usd !== undefined
              ? `Projected at expiry: ${fmtUsd(env.projected_usd)}`
              : ""
          }
          placement="left"
        >
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {fmtUsd(env.incurred_usd)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Link
          component={RouterLink}
          to={`/exports/${env.workflow_id}`}
          variant="caption"
        >
          <code>{env.workflow_id.slice(0, 8)}…</code>
        </Link>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {formatTs(env.submitted_at)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
